import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Coins, X, Check, Sparkles, Loader2, Rocket, Gift, Wand2, Image as ImageIcon, Tag, Users } from "lucide-react";
import { authApi } from "./authApi";
import { VoiceTextBox } from "./AIAssist";
import { suggestionsForRoom } from "./stagingSuggestions";

const TIER_ORDER = ["classique", "pro", "premium"];
const STAGING_STYLE_LIST = [
  { key: "contemporain", label: "Contemporain" },
  { key: "scandinave", label: "Scandinave" },
  { key: "minimaliste", label: "Minimaliste" },
  { key: "cosy", label: "Cosy" },
  { key: "boheme", label: "Bohème nature" },
  { key: "boheme_chic", label: "Bohème chic" },
  { key: "mediterraneen", label: "Méditerranéen" },
  { key: "japandi", label: "Japandi" },
  { key: "industriel", label: "Industriel" },
  { key: "classique", label: "Classique français" },
  { key: "luxe", label: "Luxe / Prestige", premium: true },
];
// Pièces/zones (clé + libellé), miroir de la Visite IA & du backend classify-room.
const STAGING_ROOMS = [
  ["salon", "Salon"], ["salle_a_manger", "Salle à manger"], ["cuisine", "Cuisine"],
  ["chambre", "Chambre"], ["salle_de_bain", "Salle de bain"], ["wc", "WC"],
  ["bureau", "Bureau"], ["entree", "Entrée"], ["facade", "Façade"],
  ["jardin", "Jardin"], ["terrasse", "Terrasse"], ["piscine", "Piscine"],
  ["vue_exterieure", "Vue extérieure"], ["garage", "Garage"], ["cave", "Cave"],
  ["dependance", "Dépendance"], ["autre", "Autre"],
];
const roomLabel = (k) => (STAGING_ROOMS.find(([rk]) => rk === k) || [null, "Salon"])[1];
const TIER_ALIAS = { standard: "classique", prestige: "premium", premium: "premium" };

export function StagingStudioModal({ listing, config, onClose, onDone, onChangeTier }) {
  const images = listing?.images?.length ? listing.images : (listing?.cover ? [listing.cover] : []);
  const tierKey = (config?.tiers && config.tiers[listing?.tier]) ? listing.tier : (TIER_ALIAS[listing?.tier] || "classique");
  const tier = config?.tiers?.[tierKey] || {};
  const cap = Number(tier.max_staging || 6);
  const tierLabel = tier.label || "Classique";
  const current = (listing?.staging || []).length;
  const remaining = Math.max(0, cap - current);

  const [selImgs, setSelImgs] = useState(images[0] ? [images[0]] : []);
  const [selStyles, setSelStyles] = useState(["contemporain"]);
  const [room, setRoom] = useState("salon");
  const [classifying, setClassifying] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [phase, setPhase] = useState("choose"); // choose | processing | done | error
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const price = Number(listing?.price || 0);
  const styleList = STAGING_STYLE_LIST.filter((s) => !s.premium || price >= 299999);

  const count = selImgs.length * selStyles.length;
  const exceeds = current + count > cap;
  // Sélection d'une photo → présélection auto de la pièce via l'IA (comme la Visite IA).
  const classifyFor = async (img) => {
    setClassifying(true);
    try {
      const d = await authApi.classifyRoom(img);
      if (d && d.room) setRoom(d.room);
    } catch (_) {} finally { setClassifying(false); }
  };
  const toggleImg = (img) => setSelImgs((s) => {
    if (s.includes(img)) return s.filter((x) => x !== img);
    classifyFor(img);
    return [...s, img];
  });
  const toggleStyle = (k) => setSelStyles((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const start = async () => {
    setErr("");
    if (count === 0) { setErr("Sélectionnez au moins une photo et un style."); return; }
    if (exceeds) { setErr(`Cette sélection (${count} rendus) dépasse votre offre ${tierLabel} — ${remaining} rendu(s) restant(s).`); return; }
    try {
      await authApi.stagingBatch(listing.slug, { images: selImgs, styles: selStyles, room: roomLabel(room), instruction });
      setPhase("processing");
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries++;
        try {
          const l = await authApi.getListing(listing.slug);
          if (l.generation_status === "done") { clearInterval(pollRef.current); setResult(l); setPhase("done"); onDone && onDone(); }
          else if (l.generation_status === "error" || tries > 90) { clearInterval(pollRef.current); setErr("La génération a échoué, réessayez."); setPhase("error"); }
        } catch (_) {}
      }, 4000);
    } catch (e) {
      setErr((e.detail && (e.detail.message || e.detail)) || e.message || "Génération impossible");
      setPhase("error");
    }
  };

  const staging = result?.staging || listing?.staging || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm overflow-y-auto py-10 px-4" data-testid="staging-studio-modal" onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-4xl mx-auto bg-[#0d0d10] border border-white/10 rounded-[5px] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-light text-white flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#C19A6B]" /> Home Staging IA avancé</h3>
          <button onClick={onClose} data-testid="staging-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {phase === "choose" && (
          <>
            <p className="text-sm text-zinc-400 mb-5">Sélectionnez plusieurs photos et plusieurs styles : l'IA génère un rendu meublé pour chaque combinaison. Ces rendus sont <b className="text-white">inclus dans votre offre {tierLabel}</b>.</p>

            {images.length === 0 ? (
              <p className="text-sm text-amber-400" data-testid="staging-no-image">Ajoutez d'abord au moins une photo à votre annonce (via « Modifier »).</p>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Photos ({selImgs.length} sélectionnée{selImgs.length > 1 ? "s" : ""})</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => toggleImg(img)} data-testid={`staging-photo-${i}`}
                      className={`relative aspect-[4/3] overflow-hidden rounded-[5px] border-2 transition-colors ${selImgs.includes(img) ? "border-[#C19A6B]" : "border-transparent hover:border-white/30"}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      {selImgs.includes(img) && <span className="absolute top-1 right-1 bg-[#C19A6B] text-white rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
                    </button>
                  ))}
                </div>

                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Styles</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {styleList.map((s) => (
                    <button key={s.key} onClick={() => toggleStyle(s.key)} data-testid={`staging-style-${s.key}`}
                      className={`text-xs uppercase tracking-wider px-4 py-2 rounded-[5px] border transition-colors ${selStyles.includes(s.key) ? "bg-[#C19A6B] border-[#C19A6B] text-white" : "border-white/15 text-zinc-300 hover:border-[#C19A6B]/60"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>

                <div className="mb-6">
                  <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                    Pièce
                    {classifying && <span className="inline-flex items-center gap-1 text-[#C19A6B] normal-case tracking-normal" data-testid="staging-room-detecting"><Loader2 className="w-3 h-3 animate-spin" /> détection auto…</span>}
                  </p>
                  <select value={room} onChange={(e) => setRoom(e.target.value)} disabled={classifying} data-testid="staging-room" className="bg-white/5 border border-white/12 rounded-[5px] px-4 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B] disabled:opacity-60">
                    {STAGING_ROOMS.map(([rk, rl]) => <option key={rk} value={rk} className="bg-[#0d0d10]">{rl}</option>)}
                  </select>
                  <p className="text-[10px] text-zinc-500 mt-1">Pré-remplie automatiquement selon la photo sélectionnée — ajustable si besoin.</p>
                </div>

                <div className="mb-6">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#C19A6B] mb-2"><Wand2 className="w-3.5 h-3.5" /> Précision <span className="text-zinc-500 normal-case tracking-normal">(facultatif)</span></p>
                  <VoiceTextBox value={instruction} onChange={setInstruction} testid="staging-instruction" minHeight="min-h-[60px]"
                    placeholder="Ex : « ajouter une cheminée allumée », « table dressée pour 6 », « enfants qui jouent »…" />
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="staging-suggestions">
                    {suggestionsForRoom(room).map((s) => (
                      <button key={s} type="button" onClick={() => setInstruction((v) => [v.trim(), s].filter(Boolean).join(", "))}
                        className="text-[10px] px-2 py-1 rounded-full border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/15 transition-colors">+ {s}</button>
                    ))}
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1">Appliqué à tous les rendus de ce lot, quel que soit le style. Vide = mise en scène habituelle.</p>
                </div>

                {/* Récapitulatif du coût / plafond inclus */}
                <div data-testid="staging-recap" className={`rounded-[5px] border p-4 mb-4 ${exceeds ? "border-red-500/40 bg-red-500/5" : "border-[#C19A6B]/30 bg-[#C19A6B]/5"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                    <span className="text-zinc-300">{selImgs.length} photo(s) × {selStyles.length} style(s) = <b className="text-white" data-testid="staging-count">{count}</b> rendu(s)</span>
                    <span className="text-zinc-400">Déjà utilisés : <b className="text-white">{current}</b> / {cap} inclus · <b className={exceeds ? "text-red-400" : "text-[#C19A6B]"}>{remaining}</b> restant(s)</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className={`h-full ${exceeds ? "bg-red-500" : "bg-[#C19A6B]"}`} style={{ width: `${Math.min(100, ((current + count) / cap) * 100)}%` }} />
                  </div>
                  {exceeds && (
                    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs text-red-400" data-testid="staging-exceeds">Sélection trop importante pour votre offre {tierLabel}. Réduisez le nombre de rendus ou passez à une offre supérieure pour cette annonce.</p>
                      {onChangeTier && tierKey !== "premium" && (
                        <button onClick={() => onChangeTier(listing)} data-testid="staging-upgrade-tier" className="shrink-0 inline-flex items-center gap-1.5 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-medium uppercase tracking-wider px-4 py-2 rounded-[5px] transition-colors"><Sparkles className="w-3.5 h-3.5" /> Changer de formule</button>
                      )}
                    </div>
                  )}
                </div>

                {err && <p className="text-sm text-red-400 mb-3" data-testid="staging-error-msg">{err}</p>}
                <div className="flex justify-end">
                  <button onClick={start} disabled={count === 0 || exceeds} data-testid="staging-generate" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 rounded-[5px] transition-colors">
                    <Wand2 className="w-4 h-4" /> Générer {count > 0 ? `${count} rendu(s)` : ""}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {phase === "processing" && (
          <div className="text-center py-10" data-testid="staging-processing">
            <Loader2 className="w-12 h-12 text-[#C19A6B] mx-auto mb-4 animate-spin" />
            <p className="text-white font-light">L'IA met en scène vos {count} rendu(s)…</p>
            <p className="text-sm text-zinc-500 mt-2">Comptez ~30 s par rendu. Ne fermez pas cette fenêtre.</p>
          </div>
        )}

        {phase === "done" && (
          <div data-testid="staging-done">
            <p className="text-white text-sm mb-4 flex items-center gap-1.5"><Check className="w-4 h-4 text-[#C19A6B]" /> Rendus générés et ajoutés à votre annonce.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {staging.map((s) => (
                <figure key={s.id} className="border border-white/10 rounded-[5px] overflow-hidden">
                  <img src={s.after} alt={s.style} className="w-full aspect-[4/3] object-cover" />
                  <figcaption className="text-[10px] uppercase tracking-wider text-[#C19A6B] px-2 py-1.5">{s.style} · {s.room}</figcaption>
                </figure>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={onClose} data-testid="staging-finish" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 rounded-[5px] transition-colors">Terminer</button>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="text-center py-8" data-testid="staging-error">
            <p className="text-red-400 mb-4">{err}</p>
            <button onClick={() => { setPhase("choose"); setErr(""); }} className="border border-white/15 text-zinc-200 hover:border-[#C19A6B] hover:text-[#C19A6B] uppercase tracking-wider text-sm px-6 py-3 rounded-[5px] transition-colors">Réessayer</button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export function WalletBar({ wallet, onBuy }) {
  const credits = wallet?.credits ?? 0;
  const freeLeft = wallet?.free_listings_remaining;
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border border-[#C19A6B]/30 bg-gradient-to-r from-[#C19A6B]/10 to-transparent px-5 py-4 mb-6" data-testid="wallet-bar">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#C19A6B]/20 flex items-center justify-center"><Coins className="w-5 h-5 text-[#C19A6B]" /></div>
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-400">Mon portefeuille</p>
          <p className="text-2xl font-light text-white" data-testid="wallet-balance">{credits} <span className="text-sm text-[#C19A6B]">crédit{credits > 1 ? "s" : ""}</span></p>
        </div>
      </div>
      <button onClick={onBuy} data-testid="buy-credits-btn" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-5 py-2.5 transition-colors">
        <Coins className="w-4 h-4" /> Acheter des crédits
      </button>
    </div>
  );
}

export function BuyCreditsModal({ config, pendingSlug = "", pendingTier = "classique", onClose, onCredited }) {
  const [loading, setLoading] = useState("");
  const [err, setErr] = useState("");
  const [promo, setPromo] = useState("");
  const [promoMsg, setPromoMsg] = useState("");
  const [percentPromo, setPercentPromo] = useState(null); // {code, percent_off}
  const [applying, setApplying] = useState(false);
  const packs = config?.packs || [];

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setPromoMsg(""); setErr(""); setApplying(true);
    try {
      const r = await authApi.redeemPromo(promo.trim());
      if (r.type === "credits") {
        setPromoMsg(r.message); setPercentPromo(null); setPromo("");
        if (onCredited) onCredited();
      } else {
        setPercentPromo({ code: r.code, percent_off: r.percent_off });
        setPromoMsg(r.message);
      }
    } catch (e) { setPromoMsg(""); setErr(e.message || "Code invalide"); }
    setApplying(false);
  };

  const buy = async (lookup_key) => {
    setErr(""); setLoading(lookup_key);
    try {
      const { checkout_url } = await authApi.checkout(lookup_key, pendingSlug, pendingTier, percentPromo?.code || "");
      window.location.href = checkout_url;
    } catch (e) {
      setErr(e.message || "Paiement indisponible"); setLoading("");
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm overflow-y-auto py-10 px-4" data-testid="buy-credits-modal" onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-3xl mx-auto bg-[#0d0d10] border border-white/10 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-light text-white flex items-center gap-2"><Coins className="w-5 h-5 text-[#C19A6B]" /> Recharger mon portefeuille</h3>
          <button onClick={onClose} data-testid="buy-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-4">{pendingSlug ? "Ajoutez des crédits pour publier votre annonce — elle sera publiée automatiquement après paiement." : `1 crédit = ${config?.price_per_credit_eur ?? 10} € · paiement sécurisé Stripe.`}</p>

        {/* Code promo */}
        <div className="flex flex-wrap items-center gap-2 mb-5 border border-white/10 p-3">
          <Tag className="w-4 h-4 text-[#C19A6B]" />
          <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="Code promo" data-testid="promo-input"
            className="flex-1 min-w-[140px] bg-transparent border-b border-white/15 focus:border-[#C19A6B] outline-none text-sm text-white py-1" />
          <button onClick={applyPromo} disabled={applying} data-testid="promo-apply" className="text-xs uppercase tracking-wider bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 transition-colors">{applying ? "…" : "Appliquer"}</button>
          {percentPromo && <span className="text-xs text-green-400" data-testid="promo-active">−{percentPromo.percent_off}% appliqué</span>}
        </div>
        {promoMsg && <p className="text-sm text-green-400 -mt-3 mb-3" data-testid="promo-msg">{promoMsg}</p>}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packs.map((p) => (
            <button key={p.lookup_key} onClick={() => buy(p.lookup_key)} disabled={!!loading} data-testid={`pack-${p.lookup_key}`}
              className={`relative text-left border p-5 transition-colors ${p.popular ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 hover:border-[#C19A6B]/50"}`}>
              {p.popular && <span className="absolute -top-2.5 left-4 bg-[#C19A6B] text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">Populaire</span>}
              <p className="text-3xl font-light text-white">{p.credits}</p>
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3">crédit{p.credits > 1 ? "s" : ""}</p>
              <p className="text-lg text-[#C19A6B]">{percentPromo ? <><span className="line-through text-zinc-500 text-sm mr-1">{p.price_eur} €</span>{(p.price_eur * (1 - percentPromo.percent_off / 100)).toFixed(2)} €</> : `${p.price_eur} €`}</p>
              {p.save && !percentPromo && <p className="text-[11px] text-green-400 mt-1">{p.save}</p>}
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs text-white/80">
                {loading === p.lookup_key ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirection…</> : <>Choisir</>}
              </span>
            </button>
          ))}
        </div>
        {err && <p className="text-sm text-red-400 mt-4" data-testid="buy-error">{err}</p>}
        <p className="text-[11px] text-zinc-500 mt-5">Paiement test : carte 4242 4242 4242 4242, date future, CVC quelconque.</p>
      </motion.div>
    </motion.div>
  );
}

export function CommissionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { authApi.commissions().then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false)); }, []);

  if (loading) return <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!data) return <p className="text-zinc-500">Indisponible.</p>;

  const rates = data.rates || [];
  return (
    <div className="space-y-8" data-testid="commissions-tab">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/10 p-5">
          <p className="text-xs uppercase tracking-wider text-zinc-400">Crédits commissionnés</p>
          <p className="text-3xl font-light text-[#C19A6B] mt-1" data-testid="commission-total">{data.total_commission_credits}</p>
        </div>
        <div className="border border-white/10 p-5">
          <p className="text-xs uppercase tracking-wider text-zinc-400">Filleuls actifs</p>
          <p className="text-3xl font-light text-white mt-1" data-testid="active-referrals">{data.active_referrals}</p>
        </div>
        <div className="border border-white/10 p-5">
          <p className="text-xs uppercase tracking-wider text-zinc-400">Taux par niveau</p>
          <p className="text-sm text-zinc-300 mt-2">{rates.map((r, i) => `N${i + 1}: ${r}%`).join(" · ")}</p>
        </div>
      </div>

      <div className="border border-white/10 p-5">
        <h3 className="text-sm uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#C19A6B]" /> Mon réseau</h3>
        {data.downline.length === 0 && <p className="text-sm text-zinc-500">Aucun filleul pour l'instant. Partagez votre lien de parrainage !</p>}
        <div className="space-y-4">
          {data.downline.map((lvl) => (
            <div key={lvl.level} data-testid={`downline-level-${lvl.level}`}>
              <p className="text-xs uppercase tracking-wider text-[#C19A6B] mb-2">Niveau {lvl.level} — {lvl.count} filleul{lvl.count > 1 ? "s" : ""} ({lvl.active} actif{lvl.active > 1 ? "s" : ""})</p>
              <div className="flex flex-wrap gap-2">
                {lvl.members.map((mm, i) => (
                  <span key={i} className={`text-xs px-3 py-1.5 border ${mm.active ? "border-green-500/50 text-green-300" : "border-white/15 text-zinc-400"}`}>
                    {mm.name || "—"} {mm.active ? "· actif" : "· en attente"}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-white/10 p-5">
        <h3 className="text-sm uppercase tracking-wider text-zinc-400 mb-3">Historique des commissions</h3>
        {data.entries.length === 0 && <p className="text-sm text-zinc-500">Vos commissions apparaîtront ici dès qu'un filleul achètera des crédits.</p>}
        <div className="space-y-2">
          {data.entries.map((e) => (
            <div key={e.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
              <span className="text-zinc-300">Niveau {e.level} · achat de {e.from_name || "un filleul"}</span>
              <span className="text-[#C19A6B]">+{e.commission_credits} crédit{e.commission_credits > 1 ? "s" : ""}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PreviewModal({ listing, wallet, onClose, onDone }) {
  const images = listing?.images?.length ? listing.images : (listing?.cover ? [listing.cover] : []);
  const [selected, setSelected] = useState(images[0] || "");
  const [phase, setPhase] = useState("choose"); // choose | processing | done | error
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const start = async () => {
    setErr("");
    try {
      await authApi.generatePreview(listing.slug, selected, "Salon");
      setPhase("processing");
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries++;
        try {
          const l = await authApi.getListing(listing.slug);
          if (l.generation_status === "done") { clearInterval(pollRef.current); setResult(l); setPhase("done"); }
          else if (l.generation_status === "error" || tries > 30) { clearInterval(pollRef.current); setErr("La génération a échoué, réessayez."); setPhase("error"); }
        } catch (_) {}
      }, 4000);
    } catch (e) {
      if (e.status === 402) { setErr(e.detail?.message || e.message); setPhase("error"); }
      else { setErr(e.message || "Génération impossible"); setPhase("error"); }
    }
  };

  const staging = result?.staging || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm overflow-y-auto py-10 px-4" data-testid="preview-modal" onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-4xl mx-auto bg-[#0d0d10] border border-white/10 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-light text-white flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#C19A6B]" /> Aperçu IA gratuit</h3>
          <button onClick={onClose} data-testid="preview-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {phase === "choose" && (
          <>
            <p className="text-sm text-zinc-400 mb-4">Choisissez la photo à mettre en scène. L'IA génère le texte, le SEO et <b className="text-white">2 home staging</b> (Contemporain + Scandinave), gratuitement.</p>
            {images.length === 0 ? (
              <p className="text-sm text-amber-400" data-testid="preview-no-image">Ajoutez d'abord au moins une photo à votre annonce (via « Modifier »).</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setSelected(img)} data-testid={`preview-photo-${i}`}
                    className={`relative aspect-[4/3] overflow-hidden border-2 transition-colors ${selected === img ? "border-[#C19A6B]" : "border-transparent hover:border-white/30"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    {selected === img && <span className="absolute top-1 right-1 bg-[#C19A6B] text-white rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button onClick={start} disabled={!selected} data-testid="preview-generate" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">
                <Wand2 className="w-4 h-4" /> Générer l'aperçu gratuit
              </button>
            </div>
          </>
        )}

        {phase === "processing" && (
          <div className="text-center py-10" data-testid="preview-processing">
            <Loader2 className="w-12 h-12 text-[#C19A6B] mx-auto mb-4 animate-spin" />
            <p className="text-white font-light">L'IA rédige votre annonce et met en scène votre bien…</p>
            <p className="text-sm text-zinc-500 mt-2">Cela prend environ une minute. Ne fermez pas cette fenêtre.</p>
          </div>
        )}

        {phase === "done" && result && (
          <div data-testid="preview-done">
            <p className="text-green-400 text-sm mb-4 flex items-center gap-1.5"><Check className="w-4 h-4" /> Aperçu généré. Voici un extrait :</p>
            {result.summary && <p className="text-zinc-200 italic mb-3">« {result.summary} »</p>}
            {result.description && <p className="text-sm text-zinc-400 line-clamp-4 mb-4">{result.description}</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              {staging.map((s) => (
                <figure key={s.id} className="border border-white/10">
                  <img src={s.after} alt={s.style} className="w-full aspect-[4/3] object-cover" />
                  <figcaption className="text-[11px] uppercase tracking-wider text-[#C19A6B] px-2 py-1.5">{s.style}</figcaption>
                </figure>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={() => { onDone && onDone(); onClose(); }} data-testid="preview-finish" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">Voir mon annonce</button>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="text-center py-8" data-testid="preview-error">
            <p className="text-red-400 mb-4">{err}</p>
            <button onClick={onClose} className="border border-white/15 text-zinc-200 hover:border-[#C19A6B] hover:text-[#C19A6B] uppercase tracking-wider text-sm px-6 py-3 transition-colors">Fermer</button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export function PublishModal({ listing, config, wallet, onClose, onPublished, onNeedCredits }) {
  const tiers = config?.tiers || {};
  const [tier, setTier] = useState(listing?.tier && tiers[listing.tier] ? listing.tier : "classique");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const balance = wallet?.credits ?? 0;
  const cost = tiers[tier]?.cost ?? 1;

  const confirm = async () => {
    setErr(""); setLoading(true);
    try {
      const r = await authApi.publishListing(listing.slug, tier);
      onPublished(r);
    } catch (e) {
      if (e.status === 402) { onNeedCredits(listing.slug, tier); return; }
      setErr(e.message || "Publication impossible"); setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm overflow-y-auto py-10 px-4" data-testid="publish-modal" onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-3xl mx-auto bg-[#0d0d10] border border-white/10 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-light text-white flex items-center gap-2"><Rocket className="w-5 h-5 text-[#C19A6B]" /> Publier « {listing.title} »</h3>
          <button onClick={onClose} data-testid="publish-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-6">Choisissez le niveau de votre annonce. Le coût est débité de votre portefeuille (solde : <span className="text-[#C19A6B]">{balance} crédit{balance > 1 ? "s" : ""}</span>).</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {TIER_ORDER.filter((k) => tiers[k]).map((k) => {
            const t = tiers[k];
            const active = tier === k;
            return (
              <button key={k} onClick={() => setTier(k)} data-testid={`tier-${k}`}
                className={`text-left border p-5 transition-colors ${active ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 hover:border-white/30"}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white font-medium">{t.label}</p>
                  {active && <Check className="w-4 h-4 text-[#C19A6B]" />}
                </div>
                <p className="text-2xl font-light text-[#C19A6B] mb-3">{t.cost} <span className="text-xs text-zinc-500">crédit{t.cost > 1 ? "s" : ""}</span></p>
                <ul className="space-y-1.5">
                  {(t.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-zinc-400"><Sparkles className="w-3 h-3 text-[#C19A6B] mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
        {err && <p className="text-sm text-red-400 mt-4" data-testid="publish-error">{err}</p>}
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-zinc-300">Coût : <span className="text-[#C19A6B] font-medium">{cost} crédit{cost > 1 ? "s" : ""}</span>{balance < cost && <span className="text-amber-400"> · solde insuffisant</span>}</p>
          <button onClick={confirm} disabled={loading} data-testid="confirm-publish" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Publication…</> : balance < cost ? <><Coins className="w-4 h-4" /> Payer & publier</> : <><Rocket className="w-4 h-4" /> Publier mon annonce</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Changer la formule d'une annonce existante (upgrade/downgrade).
export function ChangeTierModal({ listing, config, wallet, onClose, onChanged, onNeedCredits }) {
  const tiers = config?.tiers || {};
  const alias = { standard: "classique", prestige: "premium" };
  const current = tiers[listing?.tier] ? listing.tier : (alias[listing?.tier] || "classique");
  const [tier, setTier] = useState(current);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const balance = wallet?.credits ?? 0;
  const published = listing?.published !== false;
  const costOf = (k) => tiers[k]?.cost ?? 1;
  const delta = published ? Math.max(0, Math.round((costOf(tier) - costOf(current)) * 100) / 100) : 0;

  const confirm = async () => {
    if (tier === current) { onClose(); return; }
    setErr(""); setLoading(true);
    try {
      const r = await authApi.changeTier(listing.slug, tier);
      onChanged(r);
    } catch (e) {
      if (e.status === 402) { onNeedCredits(listing.slug, tier); return; }
      setErr(e.message || "Changement impossible"); setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm overflow-y-auto py-10 px-4" data-testid="change-tier-modal" onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-3xl mx-auto bg-[#0d0d10] border border-white/10 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-light text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C19A6B]" /> Changer la formule</h3>
          <button onClick={onClose} data-testid="change-tier-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-2">Formule actuelle : <span className="text-white">{tiers[current]?.label || current}</span>. Votre travail (photos, textes, home staging) est conservé.</p>
        <p className="text-xs text-zinc-500 mb-6">{published ? <>Solde : <span className="text-[#C19A6B]">{balance} crédit{balance > 1 ? "s" : ""}</span>. En cas de montée en gamme, seule la <b className="text-zinc-300">différence de crédits</b> est débitée (aucun remboursement en cas de descente).</> : "Annonce en prévisualisation : le changement est gratuit, les crédits seront prélevés à la publication."}</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {TIER_ORDER.filter((k) => tiers[k]).map((k) => {
            const t = tiers[k];
            const active = tier === k;
            const isCurrent = k === current;
            return (
              <button key={k} onClick={() => setTier(k)} data-testid={`change-tier-${k}`}
                className={`text-left border p-5 transition-colors relative ${active ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 hover:border-white/30"}`}>
                {isCurrent && <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider bg-white/10 text-zinc-300 px-1.5 py-0.5">Actuelle</span>}
                <p className="text-white font-medium mb-2">{t.label}</p>
                <p className="text-2xl font-light text-[#C19A6B] mb-3">{t.cost} <span className="text-xs text-zinc-500">crédit{t.cost > 1 ? "s" : ""}</span></p>
                <ul className="space-y-1.5">
                  {(t.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-zinc-400"><Sparkles className="w-3 h-3 text-[#C19A6B] mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
        {err && <p className="text-sm text-red-400 mt-4" data-testid="change-tier-error">{err}</p>}
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-zinc-300">{tier === current ? "Aucun changement" : delta > 0 ? <>À débiter : <span className="text-[#C19A6B] font-medium">{delta} crédit{delta > 1 ? "s" : ""}</span>{balance < delta && <span className="text-amber-400"> · solde insuffisant</span>}</> : (published ? "Descente de gamme — sans frais (non remboursé)" : "Gratuit en prévisualisation")}</p>
          <button onClick={confirm} disabled={loading || tier === current} data-testid="confirm-change-tier" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> …</> : delta > 0 && balance < delta ? <><Coins className="w-4 h-4" /> Payer & changer</> : <><Check className="w-4 h-4" /> Appliquer la formule</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}